import { Activity, iconForActivityType, TActivityWithNode } from '@valuecase/common'
import { useMemo } from 'react'
import { ActivityTextRender } from './ActivityText'
import { cn } from '../../lib/utils'
import { useDateFormatting } from '../../utils'
import { IonIcon } from '../../Icons/IonIcon'
import { Badge } from '../../components/ui'

function ActivityTextContainer({
  activity,
  onShowPageOrBlock,
  onShowActivityTaskDetails,
  getBlockTitle,
}: {
  activity: Activity
  onShowPageOrBlock: (e: React.MouseEvent<HTMLElement>, activity: Activity) => void
  onShowActivityTaskDetails: (activity: Activity) => void
  getBlockTitle: (data: any) => string | undefined
}) {
  // Getting out of scope untying all these knots, and given the legacy nature of the code below, will leave as a workaround
  // To the future engineer, blame @Rupenieks
  type TemporaryLegacyActivity = TActivityWithNode & {
    metadata: {
      milestoneName?: string
      taskName?: string
      assigneeName?: string
      dueDate?: string
      action?: string
      quickActionName?: string
      listItemTitle?: string
      fileName?: string
    } & Record<string, any>
    page: Record<string, any>
  }

  const { metadata, page } = activity as TemporaryLegacyActivity

  return (
    <ActivityTextRender
      activity={activity}
      onShowPageOrBlock={onShowPageOrBlock}
      onShowActivityTaskDetails={onShowActivityTaskDetails}
      getBlockTitle={getBlockTitle}
      metadata={metadata}
      page={page}
    />
  )
}

export function ActivityItem({
  activity,
  latestTimeReadActivities,
  showName = false,
  onShowPageOrBlock,
  onShowActivityTaskDetails,
  getBlockTitle,
}: {
  activity: Activity
  latestTimeReadActivities: Date | null
  showName?: boolean
  onShowPageOrBlock: (e: React.MouseEvent<HTMLElement>, activity: Activity) => void
  onShowActivityTaskDetails: (activity: Activity) => void
  getBlockTitle: (data: any) => string | undefined
}) {
  const { formatTime } = useDateFormatting()
  const newActivity = useMemo(
    () => latestTimeReadActivities && new Date(activity.createdAt) > latestTimeReadActivities,
    [activity, latestTimeReadActivities],
  )

  return (
    <div className={'flex gap-2'}>
      <div className={'w-4 flex'}>
        <IonIcon className={'text-grey-s4 w-4 h-4'} name={iconForActivityType(activity.type)} />
      </div>
      <div className={'flex flex-col gap-1 text-grey-s5 text-xs flex-grow'}>
        <p className={cn({ 'first-letter:capitalize': !showName })}>
          {showName && (
            <>
              <span className={'text-grey-s6 font-medium'}>
                {activity.name || activity.email || 'A stakeholder'}
              </span>{' '}
            </>
          )}
          <ActivityTextContainer
            activity={activity}
            onShowPageOrBlock={onShowPageOrBlock}
            onShowActivityTaskDetails={onShowActivityTaskDetails}
            getBlockTitle={getBlockTitle}
          />
        </p>
        <p className={'text-grey-s4'}>{formatTime(activity.createdAt)}</p>
      </div>
      {newActivity && (
        <div className={'flex flex-col'}>
          <Badge variant={'subtle-info'} size={'small'}>
            New
          </Badge>
        </div>
      )}
    </div>
  )
}
